import { format, parseISO } from "date-fns";
import { MoreHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CompanyLogo, getDomainFromCompany } from "../../../../components/ui/CompanyLogo";
import { Popover, PopoverTrigger, PopoverContent } from "../../../../components/ui/popover";
import { Input } from "../../../../components/ui/input";
import { updateTask } from "../../../../services/tasksEndpoints";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import type { TaskRecord } from "../tasksTypes";
import { PRIORITY_DOT_COLORS } from "../tasksTypes";

type MemberInfo = { name: string; email: string; initials: string };

type TaskCardProps = {
  task: TaskRecord;
  onClick?: (task: TaskRecord) => void;
  members?: any[];
  memberLookup?: Record<string, MemberInfo>;
};

const formatTaskId = (externalId: string) => {
  if (!externalId) return "";
  const match = externalId.match(/(\d+)$/);
  if (match) return `#${match[1].slice(-4)}`;
  return `#${externalId.slice(-4).toLowerCase()}`;
};

const getTaskTitle = (task: TaskRecord): string =>
  task.subject ||
  task.native_subject ||
  task.description ||
  task.native_description ||
  task.evidence_text ||
  "Untitled task";

const TaskCard = ({ task, onClick, members = [], memberLookup = {} }: TaskCardProps) => {
  const companyDomain = task.account_domain || (task.account_name ? getDomainFromCompany(task.account_name) : "");
  const priorityColor = PRIORITY_DOT_COLORS[task.priority?.toLowerCase() || ""];
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const workspace = useWorkspaceStore((s) => s.workspace);
  const queryClient = useQueryClient();

  // Resolve assigned_to (could be member_id UUID or email) to display info
  const assigneeInfo = useMemo(() => {
    if (!task.assigned_to) return null;
    return memberLookup[task.assigned_to] || { name: task.assigned_to, email: task.assigned_to, initials: task.assigned_to.charAt(0).toUpperCase() };
  }, [task.assigned_to, memberLookup]);

  const filteredMembers = useMemo(() => {
    if (!memberSearch) return members;
    const q = memberSearch.toLowerCase();
    return members.filter((m: any) => {
      const name = `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase();
      return name.includes(q) || (m.email || "").toLowerCase().includes(q);
    });
  }, [members, memberSearch]);

  const assignMutation = useMutation({
    mutationFn: (assignedTo: string) =>
      updateTask({
        workspace_id: workspace!.id,
        task_external_id: task.external_id,
        updates: { assigned_to: assignedTo } as Partial<TaskRecord>,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks-page", workspace?.id] });
      setAssigneeOpen(false);
    },
  });

  return (
    <div
      className="rounded-lg border border-border bg-card p-3 transition-colors hover:border-border-chip cursor-pointer space-y-2"
      onClick={() => onClick?.(task)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick?.(task); }}
    >
      {/* Top line: Task ID + date */}
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span className="font-medium">{formatTaskId(task.external_id)}</span>
        {task.created_at && (
          <>
            <span>/</span>
            <span>{format(parseISO(task.created_at), "MMM d, yyyy")}</span>
          </>
        )}
      </div>

      {/* Description */}
      <p className="text-[13px] font-medium text-foreground line-clamp-2 leading-snug">
        {getTaskTitle(task)}
      </p>

      {/* Bottom row */}
      <div className="flex items-center gap-1.5 pt-0.5">
        <MoreHorizontal className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/40" />

        {priorityColor && (
          <span
            className="h-2.5 w-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: priorityColor }}
            title={`Priority: ${task.priority}`}
          />
        )}

        <CompanyLogo
          domain={companyDomain}
          name={task.account_name || "Unknown"}
          className="h-4 w-4 rounded flex-shrink-0"
        />
        <span className="truncate text-xs text-muted-foreground max-w-[100px]">
          {task.account_name || "No account"}
        </span>

        <div className="flex-1" />

        {/* Assignee popover */}
        <Popover open={assigneeOpen} onOpenChange={(open) => { setAssigneeOpen(open); if (!open) setMemberSearch(""); }}>
          <PopoverTrigger asChild>
            <div
              className="flex items-center gap-1 flex-shrink-0 cursor-pointer hover:opacity-80"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => { if (e.key === "Enter") e.stopPropagation(); }}
              role="button"
              tabIndex={0}
            >
              {assigneeInfo ? (
                <>
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                    {assigneeInfo.initials}
                  </div>
                  <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                    {assigneeInfo.name}
                  </span>
                </>
              ) : (
                <>
                  <div className="flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-muted-foreground/30 text-[10px] text-muted-foreground/40">
                    ?
                  </div>
                  <span className="text-xs text-muted-foreground/40">Unassigned</span>
                </>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="end" onClick={(e) => e.stopPropagation()}>
            <Input
              placeholder="Search members..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.currentTarget.value)}
              className="h-7 text-xs mb-2"
            />
            <div className="max-h-48 overflow-y-auto space-y-0.5">
              {task.assigned_to && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                  onClick={() => assignMutation.mutate("")}
                >
                  <div className="flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-muted-foreground/30 text-[10px] text-muted-foreground/40">
                    ?
                  </div>
                  Unassign
                </button>
              )}
              {filteredMembers.map((member: any) => {
                const memberId = member.member_id || member.user_id;
                const name = `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email;
                const initial = (member.first_name?.[0] || member.email?.[0] || "?").toUpperCase();
                return (
                  <button
                    key={memberId}
                    type="button"
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-muted"
                    onClick={() => assignMutation.mutate(memberId)}
                  >
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground flex-shrink-0">
                      {initial}
                    </div>
                    <div className="flex flex-col items-start min-w-0">
                      <span className="truncate text-foreground max-w-[180px]">{name}</span>
                      {name !== member.email && (
                        <span className="truncate text-muted-foreground max-w-[180px]">{member.email}</span>
                      )}
                    </div>
                  </button>
                );
              })}
              {filteredMembers.length === 0 && (
                <div className="px-2 py-3 text-center text-xs text-muted-foreground">No members found</div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default TaskCard;
